import { FC, PropsWithChildren, useEffect } from "react";
import styled, { css } from "styled-components";
import { motion } from "framer-motion";

import Text from "@/components/Text";

const Dialog = styled(motion.div)`
  width: 384px;
  position: fixed;
  top: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  width: 100%;
  padding: 24px 88px 24px 32px;
  box-shadow: 0px 1px 4px rgba(34, 34, 34, 0.08);
  position: relative;
  z-index: 1;
`;

const CloseButton = styled.button`
  width: 24px;
  height: 24px;
  position: absolute;
  right: 32px;
  top: 24px;
  color: ${({ theme }) => theme.colors.black};
  transition: 0.2s ease-out opacity;
  &:hover {
    opacity: 0.75;
  }
`;

export const SidePanelContent = styled.div`
  width: 100%;
  flex: 1;
  padding: 24px 32px 56px;
  overflow-y: auto;
`;

export const SidePanelBody = styled.form`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 1px;
`;

export const SidePanelFooter = styled.div<{ $actions?: boolean }>`
  padding: 24px 32px;
  width: 100%;
  box-shadow: 0px -1px 8px rgba(34, 34, 34, 0.06);
  ${({ $actions }) =>
    $actions &&
    css`
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-column-gap: 16px;
    `}
`;

interface Props {
  onClose: () => void;
  heading: string;
  className?: string;
}

const SidePanel: FC<PropsWithChildren<Props>> = ({
  onClose,
  children,
  heading,
  className,
}) => {
  useEffect(() => {
    const callback = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      onClose();
    };
    document.addEventListener('keydown', callback);
    return () => document.removeEventListener('keydown', callback);
    // eslint-disable-next-line
  }, []);

  return (
    <Dialog
      initial={{ right: -400 }}
      animate={{ right: 0 }}
      exit={{ right: -400 }}
      transition={{ duration: 0.2 }}
      className={className}
    >
      <Header>
        <Text as="h4" $type="button">
          {heading}
        </Text>
        <CloseButton onClick={onClose}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M18 18L6 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
      </Header>
      {children}
    </Dialog>
  );
};

export default SidePanel;
